// Resizable + collapsible right-rail panels.
// Each screen wraps its right-side aside in <RailRight> instead of <aside>.
// User can drag the left edge to resize, or click the close button in the
// rail header (provided by the screen) — exposed via context — to collapse.
// State persists per-rail-id in localStorage.

const RailContext = React.createContext({ collapse: () => {} });

function useRailControls() { return React.useContext(RailContext); }

function getLS(key, fallback) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; }
  catch { return fallback; }
}
function setLS(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}
}

// Right-side rail with drag-resize handle on its left edge.
// Pass id to persist user's width/collapsed state.
// Pass defaultWidth, min/max.
function RailRight({ id, defaultWidth = 340, min = 260, max = 520, className = '', style = {}, children }) {
  const [collapsed, setCollapsed] = React.useState(() => getLS(`pp-rail-c-${id}`, false));
  const [width, setWidth] = React.useState(() => getLS(`pp-rail-w-${id}`, defaultWidth));
  const dragRef = React.useRef(null);

  React.useEffect(() => setLS(`pp-rail-c-${id}`, collapsed), [collapsed, id]);
  React.useEffect(() => setLS(`pp-rail-w-${id}`, width), [width, id]);

  const onMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev) => {
      const dx = startX - ev.clientX;
      const next = Math.min(max, Math.max(min, startW + dx));
      setWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  if (collapsed) {
    return (
      <button
        className="pp-rail-collapsed pp-rail-collapsed-right"
        onClick={() => setCollapsed(false)}
        title={(typeof useT === 'function' ? (useT().lang === 'ko' ? '패널 펴기' : 'Expand panel') : 'Expand panel')}
      >
        <I name="chevronLeft" size={14}/>
      </button>
    );
  }

  return (
    <RailContext.Provider value={{ collapse: () => setCollapsed(true) }}>
      <aside
        className={'pp-rail-resizable ' + className}
        style={{ ...style, width, flexShrink: 0 }}
      >
        <div className="pp-rail-handle pp-rail-handle-left" onMouseDown={onMouseDown} ref={dragRef}/>
        {children}
      </aside>
    </RailContext.Provider>
  );
}

// Left-side rail variant (filter rails). Drag handle on right edge.
function RailLeft({ id, defaultWidth = 260, min = 200, max = 360, className = '', style = {}, children }) {
  const [collapsed, setCollapsed] = React.useState(() => getLS(`pp-rail-c-${id}`, false));
  const [width, setWidth] = React.useState(() => getLS(`pp-rail-w-${id}`, defaultWidth));

  React.useEffect(() => setLS(`pp-rail-c-${id}`, collapsed), [collapsed, id]);
  React.useEffect(() => setLS(`pp-rail-w-${id}`, width), [width, id]);

  const onMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = width;
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      setWidth(Math.min(max, Math.max(min, startW + dx)));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  if (collapsed) {
    return (
      <button
        className="pp-rail-collapsed pp-rail-collapsed-left"
        onClick={() => setCollapsed(false)}
        title={(typeof useT === 'function' ? (useT().lang === 'ko' ? '패널 펴기' : 'Expand panel') : 'Expand panel')}
      >
        <I name="chevronRight" size={14}/>
      </button>
    );
  }

  return (
    <RailContext.Provider value={{ collapse: () => setCollapsed(true) }}>
      <aside
        className={'pp-rail-resizable ' + className}
        style={{ ...style, width, flexShrink: 0 }}
      >
        {children}
        <div className="pp-rail-handle pp-rail-handle-right" onMouseDown={onMouseDown}/>
      </aside>
    </RailContext.Provider>
  );
}

// Collapse-button — drop into a rail header. Connects to the surrounding rail context.
function RailCollapseBtn({ title }) {
  const { collapse } = useRailControls();
  return (
    <button className="iconbtn" onClick={collapse} title={title || 'Collapse'}>
      <I name="close" size={13}/>
    </button>
  );
}

if (typeof document !== 'undefined' && !document.getElementById('pp-rail-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-rail-styles';
  s.textContent = `
  .pp-rail-resizable {
    position: relative;
    height: 100%;
    flex-shrink: 0;
  }
  .pp-rail-handle {
    position: absolute; top: 0; bottom: 0;
    width: 5px;
    cursor: col-resize;
    z-index: 2;
    background: transparent;
    transition: background .12s;
  }
  .pp-rail-handle-left { left: -2px; }
  .pp-rail-handle-right { right: -2px; }
  .pp-rail-handle:hover, .pp-rail-handle:active {
    background: linear-gradient(90deg, transparent, rgba(136,150,255,0.40), transparent);
  }
  .pp-rail-collapsed {
    width: 22px;
    flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: var(--bg-1);
    color: var(--fg-3);
    cursor: pointer;
    transition: background .12s, color .12s;
    border: 0;
    padding: 0;
  }
  .pp-rail-collapsed-right { border-left: 1px solid var(--border-1); }
  .pp-rail-collapsed-left  { border-right: 1px solid var(--border-1); }
  .pp-rail-collapsed:hover {
    background: var(--bg-2);
    color: var(--acc-indigo);
  }
  `;
  document.head.appendChild(s);
}

export { RailRight, RailLeft, RailCollapseBtn, useRailControls };
